import { useState } from "react";
import CalculatorLayout from "@/components/CalculatorLayout";
import { ResultCard, BandGauge } from "@/components/ResultCard";
import AgeGenderFields from "@/components/AgeGenderFields";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  round, kgToLb, lbToKg, cmToIn, inToCm, ftInToCm, bmiCategory, parseNum,
} from "@/calculators/_helpers";

// ---------- Shared unit + height/weight inputs ----------
function UnitToggle({ value, onChange }) {
  return (
    <RadioGroup value={value} onValueChange={onChange} className="flex gap-2" data-testid="unit-toggle">
      {["metric", "imperial"].map((u) => (
        <div key={u} className="flex-1">
          <RadioGroupItem value={u} id={`unit-${u}`} className="peer sr-only" />
          <Label
            htmlFor={`unit-${u}`}
            className="flex h-10 cursor-pointer items-center justify-center rounded-full border border-border text-sm font-medium peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:border-primary"
          >
            {u === "metric" ? "Metric (kg / cm)" : "Imperial (lb / in)"}
          </Label>
        </div>
      ))}
    </RadioGroup>
  );
}

function useBody() {
  const [unit, setUnit] = useState("metric");
  const [heightCm, setHeightCm] = useState("");
  const [ft, setFt] = useState("");
  const [inches, setInches] = useState("");
  const [weight, setWeight] = useState("");

  const height_cm = unit === "metric" ? parseNum(heightCm) : ftInToCm(ft, inches);
  const weight_kg = unit === "metric" ? parseNum(weight) : lbToKg(parseNum(weight));

  const HeightInputs = (
    <div className="space-y-2">
      <Label>Height</Label>
      {unit === "metric" ? (
        <Input type="number" inputMode="decimal" data-testid="in-height-cm" placeholder="cm" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" inputMode="decimal" data-testid="in-height-ft" placeholder="ft" value={ft} onChange={(e) => setFt(e.target.value)} />
          <Input type="number" inputMode="decimal" data-testid="in-height-in" placeholder="in" value={inches} onChange={(e) => setInches(e.target.value)} />
        </div>
      )}
    </div>
  );

  const WeightInputs = (
    <div className="space-y-2">
      <Label>Weight</Label>
      <Input type="number" inputMode="decimal" data-testid="in-weight" placeholder={unit === "metric" ? "kg" : "lb"} value={weight} onChange={(e) => setWeight(e.target.value)} />
    </div>
  );

  const UnitInputs = <UnitToggle value={unit} onChange={setUnit} />;

  return { unit, height_cm, weight_kg, HeightInputs, WeightInputs, UnitInputs };
}

// ---------- 1. BMI ----------
export function BmiCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const { height_cm, weight_kg, HeightInputs, WeightInputs, UnitInputs } = useBody();
  const h_m = height_cm / 100;
  const bmi = h_m > 0 && weight_kg > 0 ? weight_kg / (h_m * h_m) : 0;
  const bmiR = round(bmi, 1);
  const cat = bmi > 0 ? bmiCategory(bmi) : null;

  return (
    <CalculatorLayout
      calc={calc}
      intro="Body Mass Index — a quick screening measure of body-weight-for-height."
      resultText={bmi > 0 ? `Your BMI is ${bmiR} — ${cat?.label}.` : null}
      formula={<p><strong>BMI = weight (kg) ÷ height (m)²</strong>. Categories follow the WHO: &lt;18.5 Underweight, 18.5–24.9 Normal, 25–29.9 Overweight, ≥30 Obese.</p>}
      faqs={[
        { q: "Is BMI accurate for athletes?", a: "BMI doesn't distinguish muscle from fat, so very muscular people may score 'overweight' despite being lean. Use body-fat % for better accuracy." },
        { q: "What's a healthy BMI?", a: "For most adults, 18.5–24.9 is considered healthy." },
        { q: "Does BMI apply to children?", a: "Children use age- and sex-specific BMI percentiles, not the adult ranges shown here." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        {UnitInputs}
        <div className="grid gap-4 sm:grid-cols-2">
          {HeightInputs}
          {WeightInputs}
        </div>
      </div>
      {bmi > 0 && (
        <div className="mt-6 space-y-4">
          <ResultCard label="Your BMI" value={bmiR} unit={cat.label} tone={cat.tone === "good" ? "good" : cat.tone === "danger" ? "danger" : "warning"} testId="bmi-result" />
          <BandGauge
            value={Math.min(40, Math.max(15, bmi))}
            min={15}
            max={40}
            bands={[
              { from: 15, to: 18.5, color: "bg-sky-400", label: "Under" },
              { from: 18.5, to: 25, color: "bg-emerald-500", label: "Normal" },
              { from: 25, to: 30, color: "bg-amber-500", label: "Over" },
              { from: 30, to: 40, color: "bg-red-500", label: "Obese" },
            ]}
          />
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- 2. Body Fat (U.S. Navy, cm) ----------
export function BodyFatCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const { height_cm, unit, HeightInputs, UnitInputs } = useBody();
  const [neck, setNeck] = useState("");
  const [waist, setWaist] = useState("");
  const [hip, setHip] = useState("");
  const conv = (v) => (unit === "metric" ? parseNum(v) : inToCm(parseNum(v)));

  const neckCm = conv(neck), waistCm = conv(waist), hipCm = conv(hip);
  let bf = 0;
  if (height_cm > 0 && neckCm > 0 && waistCm > 0 && (gender === "male" || hipCm > 0)) {
    if (gender === "male") {
      bf = 495 / (1.0324 - 0.19077 * Math.log10(waistCm - neckCm) + 0.15456 * Math.log10(height_cm)) - 450;
    } else {
      bf = 495 / (1.29579 - 0.35004 * Math.log10(waistCm + hipCm - neckCm) + 0.22100 * Math.log10(height_cm)) - 450;
    }
  }
  const bfR = round(bf, 1);
  const cat = bf <= 0 ? null : gender === "male"
    ? (bf < 6 ? "Essential" : bf < 14 ? "Athletes" : bf < 18 ? "Fitness" : bf < 25 ? "Average" : "Obese")
    : (bf < 14 ? "Essential" : bf < 21 ? "Athletes" : bf < 25 ? "Fitness" : bf < 32 ? "Average" : "Obese");

  const unitLabel = unit === "metric" ? "cm" : "in";
  return (
    <CalculatorLayout
      calc={calc}
      intro="Estimate body-fat percentage using the U.S. Navy circumference method."
      resultText={bf > 0 ? `Estimated body fat: ${bfR}% (${cat}).` : null}
      formula={<>
        <p><strong>Men:</strong> 495 / (1.0324 − 0.19077 × log10(waist − neck) + 0.15456 × log10(height)) − 450</p>
        <p><strong>Women:</strong> 495 / (1.29579 − 0.35004 × log10(waist + hip − neck) + 0.22100 × log10(height)) − 450</p>
        <p>Measurements are in centimetres. Take the waist at the navel, neck below the larynx, and hip at the widest point.</p>
      </>}
      faqs={[
        { q: "How accurate is the Navy method?", a: "It's typically within 3–4% of hydrostatic weighing when measurements are taken carefully." },
        { q: "Why do women need a hip measurement?", a: "Women carry more fat around the hips, so hip circumference improves the formula's accuracy." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        {UnitInputs}
        <div className="grid gap-4 sm:grid-cols-2">
          {HeightInputs}
          <div className="space-y-2"><Label>Neck ({unitLabel})</Label>
            <Input type="number" inputMode="decimal" data-testid="in-neck" value={neck} onChange={(e) => setNeck(e.target.value)} /></div>
          <div className="space-y-2"><Label>Waist ({unitLabel})</Label>
            <Input type="number" inputMode="decimal" data-testid="in-waist" value={waist} onChange={(e) => setWaist(e.target.value)} /></div>
          {gender === "female" && (
            <div className="space-y-2"><Label>Hip ({unitLabel})</Label>
              <Input type="number" inputMode="decimal" data-testid="in-hip" value={hip} onChange={(e) => setHip(e.target.value)} /></div>
          )}
        </div>
      </div>
      {bf > 0 && <div className="mt-6"><ResultCard label="Body fat" value={bfR} unit="%" testId="bf-result">{cat}</ResultCard></div>}
    </CalculatorLayout>
  );
}

// ---------- 3. Army Body Fat (inches) ----------
export function ArmyBodyFatCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [heightIn, setHeightIn] = useState("");
  const [neckIn, setNeckIn] = useState("");
  const [waistIn, setWaistIn] = useState("");
  const [hipIn, setHipIn] = useState("");
  const h = parseNum(heightIn), n = parseNum(neckIn), w = parseNum(waistIn), hp = parseNum(hipIn);
  let bf = 0;
  if (h > 0 && n > 0 && w > 0 && (gender === "male" || hp > 0)) {
    bf = gender === "male"
      ? 86.010 * Math.log10(w - n) - 70.041 * Math.log10(h) + 36.76
      : 163.205 * Math.log10(w + hp - n) - 97.684 * Math.log10(h) - 78.387;
  }
  const bfR = round(bf, 1);
  const pass = bf > 0 && (gender === "male" ? bf <= 26 : bf <= 36);
  return (
    <CalculatorLayout
      calc={calc}
      intro="U.S. Army tape-test body-fat estimate (inches)."
      resultText={bf > 0 ? `Army body fat: ${bfR}% — ${pass ? "within standard" : "over standard"}.` : null}
      formula={<>
        <p><strong>Men:</strong> 86.010 × log10(waist − neck) − 70.041 × log10(height) + 36.76</p>
        <p><strong>Women:</strong> 163.205 × log10(waist + hip − neck) − 97.684 × log10(height) − 78.387</p>
        <p>All measurements in <em>inches</em>. Army max standards vary by age — this calculator uses a general threshold.</p>
      </>}
      faqs={[
        { q: "How does this differ from the Navy method?", a: "Same idea (circumference-based), but different constants. Both give similar results in practice." },
        { q: "Is 26% / 36% the exact Army max?", a: "Army standards vary by age (17-20: 20%/30%, up to 40+: 26%/36% approximately). This calculator uses the highest tier as a rough pass/fail guide." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Height (in)</Label><Input type="number" data-testid="army-h" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} /></div>
          <div className="space-y-2"><Label>Neck (in)</Label><Input type="number" data-testid="army-n" value={neckIn} onChange={(e) => setNeckIn(e.target.value)} /></div>
          <div className="space-y-2"><Label>Waist (in)</Label><Input type="number" data-testid="army-w" value={waistIn} onChange={(e) => setWaistIn(e.target.value)} /></div>
          {gender === "female" && <div className="space-y-2"><Label>Hip (in)</Label><Input type="number" data-testid="army-hp" value={hipIn} onChange={(e) => setHipIn(e.target.value)} /></div>}
        </div>
      </div>
      {bf > 0 && (
        <div className="mt-6"><ResultCard label="Army body fat" value={bfR} unit="%" tone={pass ? "good" : "danger"} testId="army-bf-result">
          {pass ? "Within Army standard" : "Exceeds Army standard"}
        </ResultCard></div>
      )}
    </CalculatorLayout>
  );
}

// ---------- 4. Ideal Weight ----------
export function IdealWeightCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const { height_cm, HeightInputs, UnitInputs } = useBody();
  const inches = cmToIn(height_cm);
  const over60 = Math.max(0, inches - 60);
  let formulas = null;
  if (height_cm > 0) {
    formulas = [
      { name: "Devine", kg: gender === "male" ? 50 + 2.3 * over60 : 45.5 + 2.3 * over60 },
      { name: "Robinson", kg: gender === "male" ? 52 + 1.9 * over60 : 49 + 1.7 * over60 },
      { name: "Miller", kg: gender === "male" ? 56.2 + 1.41 * over60 : 53.1 + 1.36 * over60 },
      { name: "Hamwi", kg: gender === "male" ? 48 + 2.7 * over60 : 45.5 + 2.2 * over60 },
    ];
  }

  const resultText = formulas
    ? "Ideal weight — " + formulas.map((f) => `${f.name} ${round(f.kg, 1)}kg`).join(" · ")
    : null;

  return (
    <CalculatorLayout
      calc={calc}
      intro="Compare four classic ideal-weight formulas — Devine, Robinson, Miller, and Hamwi."
      resultText={resultText}
      formula={<p>Each formula uses the excess inches above 60 in (152 cm) with slightly different coefficients. There's no single 'right' formula — treat this as a range.</p>}
      faqs={[
        { q: "Which formula is best?", a: "Devine is the most widely cited (originally for drug dosing). Miller tends to give the lowest values, Hamwi the highest. Use the range as guidance." },
        { q: "Do these apply to very short people?", a: "For heights under 5 ft, the formulas can under-estimate — treat as approximate only." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        {UnitInputs}
        {HeightInputs}
      </div>
      {formulas && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="ideal-results">
          {formulas.map((f) => (
            <div key={f.name} className="rounded-xl border border-border p-4">
              <div className="text-xs uppercase text-muted-foreground">{f.name}</div>
              <div className="mt-1 text-2xl font-bold">{round(f.kg, 1)} kg <span className="text-sm text-muted-foreground font-normal">({round(kgToLb(f.kg), 1)} lb)</span></div>
            </div>
          ))}
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- 5. Lean Body Mass ----------
export function LeanBodyMassCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const { height_cm, weight_kg, HeightInputs, WeightInputs, UnitInputs } = useBody();
  let lbm = 0, fatMass = 0, bfPct = 0;
  if (height_cm > 0 && weight_kg > 0) {
    lbm = gender === "male"
      ? 0.407 * weight_kg + 0.267 * height_cm - 19.2
      : 0.252 * weight_kg + 0.473 * height_cm - 48.3;
    fatMass = weight_kg - lbm;
    bfPct = (fatMass / weight_kg) * 100;
  }
  return (
    <CalculatorLayout
      calc={calc}
      intro="Estimate your lean body mass (fat-free mass) using the Boer formula."
      resultText={lbm > 0 ? `LBM: ${round(lbm, 1)} kg · Fat mass ${round(fatMass, 1)} kg (${round(bfPct, 1)}%).` : null}
      formula={<>
        <p><strong>Men:</strong> LBM = 0.407 × weight + 0.267 × height − 19.2</p>
        <p><strong>Women:</strong> LBM = 0.252 × weight + 0.473 × height − 48.3</p>
        <p>Weight in kg, height in cm. Fat mass = total weight − LBM.</p>
      </>}
      faqs={[
        { q: "How is LBM different from muscle mass?", a: "LBM includes everything that isn't fat — muscle, bone, organs, water. Muscle mass alone is smaller." },
        { q: "Why does LBM matter?", a: "It's used to dose some medications and to set nutrition targets like protein intake." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        {UnitInputs}
        <div className="grid gap-4 sm:grid-cols-2">{HeightInputs}{WeightInputs}</div>
      </div>
      {lbm > 0 && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <ResultCard label="Lean body mass" value={round(lbm, 1)} unit="kg" testId="lbm-result" />
          <div className="rounded-2xl border border-border p-6">
            <div className="text-xs uppercase text-muted-foreground">Estimated fat mass</div>
            <div className="mt-2 text-3xl font-bold">{round(fatMass, 1)} <span className="text-lg text-muted-foreground font-normal">kg</span></div>
            <div className="text-sm text-muted-foreground mt-1">≈ {round(bfPct, 1)}% body fat</div>
          </div>
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- 6. Healthy Weight ----------
export function HealthyWeightCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const { height_cm, HeightInputs, UnitInputs } = useBody();
  const h_m = height_cm / 100;
  const min = h_m > 0 ? 18.5 * h_m * h_m : 0;
  const max = h_m > 0 ? 24.9 * h_m * h_m : 0;
  return (
    <CalculatorLayout
      calc={calc}
      intro="The healthy weight range for your height, based on the WHO BMI window."
      resultText={min > 0 ? `Healthy weight: ${round(min, 1)}–${round(max, 1)} kg (${round(kgToLb(min), 1)}–${round(kgToLb(max), 1)} lb).` : null}
      formula={<p>Weight range = BMI × height² for BMI values 18.5 (lower bound) and 24.9 (upper bound).</p>}
      faqs={[
        { q: "Is BMI-based 'healthy weight' perfect?", a: "It's a screening range, not a diagnosis. Highly muscular people may sit above the range without being unhealthy." },
        { q: "Should I aim for the middle of the range?", a: "Not necessarily — pick a weight where you feel strong, sleep well and move well." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        {UnitInputs}
        {HeightInputs}
      </div>
      {min > 0 && (
        <div className="mt-6"><ResultCard label="Healthy weight range" value={`${round(min, 1)}–${round(max, 1)}`} unit="kg" testId="healthy-range-result">
          {round(kgToLb(min), 1)}–{round(kgToLb(max), 1)} lb
        </ResultCard></div>
      )}
    </CalculatorLayout>
  );
}

// ---------- 7. Body Type ----------
export function BodyTypeCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const { height_cm, HeightInputs, UnitInputs, unit } = useBody();
  const [wrist, setWrist] = useState("");
  const wristCm = unit === "metric" ? parseNum(wrist) : inToCm(parseNum(wrist));
  let type = null, frame = null;
  if (height_cm > 0 && wristCm > 0) {
    const r = height_cm / wristCm;
    if (gender === "male") {
      if (r > 10.4) { frame = "Small"; type = "Ectomorph"; }
      else if (r >= 9.6) { frame = "Medium"; type = "Mesomorph"; }
      else { frame = "Large"; type = "Endomorph"; }
    } else {
      if (r > 11) { frame = "Small"; type = "Ectomorph"; }
      else if (r >= 10.1) { frame = "Medium"; type = "Mesomorph"; }
      else { frame = "Large"; type = "Endomorph"; }
    }
  }
  const desc = {
    Ectomorph: "Lean and long, difficulty building mass.",
    Mesomorph: "Athletic build, gains muscle readily.",
    Endomorph: "Softer, rounder physique with easier fat storage.",
  };
  return (
    <CalculatorLayout
      calc={calc}
      intro="Estimate your somatotype (ectomorph / mesomorph / endomorph) from your frame size."
      resultText={type ? `Body type: ${type} (${frame} frame).` : null}
      formula={<p>Ratio = height ÷ wrist circumference. The ratio maps to frame size (small/medium/large) which loosely correlates to ecto/meso/endomorph somatotypes. This is an estimation only.</p>}
      faqs={[
        { q: "Can my body type change?", a: "Genetics set your bone structure, but training and nutrition heavily influence how your physique looks." },
        { q: "Where do I measure my wrist?", a: "Just above the wrist bone on your dominant side, snug but not tight." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        {UnitInputs}
        <div className="grid gap-4 sm:grid-cols-2">
          {HeightInputs}
          <div className="space-y-2"><Label>Wrist circumference ({unit === "metric" ? "cm" : "in"})</Label>
            <Input type="number" inputMode="decimal" data-testid="in-wrist" value={wrist} onChange={(e) => setWrist(e.target.value)} /></div>
        </div>
      </div>
      {type && (
        <div className="mt-6"><ResultCard label="Body type" value={type} testId="body-type-result">{frame} frame · {desc[type]}</ResultCard></div>
      )}
    </CalculatorLayout>
  );
}

// ---------- 8. Anorexic BMI ----------
export function AnorexicBmiCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("female");
  const { height_cm, weight_kg, HeightInputs, WeightInputs, UnitInputs } = useBody();
  const h_m = height_cm / 100;
  const bmi = h_m > 0 && weight_kg > 0 ? weight_kg / (h_m * h_m) : 0;
  const bmiR = round(bmi, 1);
  const severe = bmi > 0 && bmi < 17.5;

  return (
    <CalculatorLayout
      calc={calc}
      intro="Screens for severe underweight (BMI < 17.5) — a common threshold in anorexia nervosa screening."
      resultText={bmi > 0 ? `BMI: ${bmiR} — ${severe ? "below the 17.5 threshold" : "above the 17.5 threshold"}.` : null}
      formula={<p>BMI = weight (kg) ÷ height (m)². A BMI below 17.5 is one of several clinical criteria used to screen for anorexia nervosa. This calculator does <strong>not</strong> diagnose an eating disorder.</p>}
      faqs={[
        { q: "Does BMI &lt; 17.5 mean I have anorexia?", a: "No. Diagnosis involves psychological and behavioural criteria a clinician evaluates — BMI alone isn't enough." },
        { q: "I'm worried about my eating. What can I do?", a: "Please contact a doctor or a specialist helpline in your country. You deserve support." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        {UnitInputs}
        <div className="grid gap-4 sm:grid-cols-2">{HeightInputs}{WeightInputs}</div>
      </div>
      {bmi > 0 && (
        <div className="mt-6"><ResultCard label="Your BMI" value={bmiR} tone={severe ? "danger" : "warning"} testId="anorexic-bmi-result">
          {severe ? "Severely underweight — please consult a healthcare professional." : "Above the severe-underweight threshold."}
        </ResultCard></div>
      )}
    </CalculatorLayout>
  );
}

// ---------- 9. Overweight ----------
export function OverweightCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const { height_cm, weight_kg, HeightInputs, WeightInputs, UnitInputs } = useBody();
  const h_m = height_cm / 100;
  const target = h_m > 0 ? 24.9 * h_m * h_m : 0;
  const toLose = weight_kg > 0 && target > 0 ? weight_kg - target : 0;
  const bmi = h_m > 0 && weight_kg > 0 ? weight_kg / (h_m * h_m) : 0;

  return (
    <CalculatorLayout
      calc={calc}
      intro="How many kilograms to lose to reach a healthy BMI (24.9)."
      resultText={bmi > 0 ? (toLose > 0 ? `To reach BMI 24.9, target losing ${round(toLose, 1)} kg (~${round(kgToLb(toLose), 1)} lb).` : `You're already at or below BMI 24.9.`) : null}
      formula={<p>Target weight = 24.9 × height² (m). "Weight to lose" = current − target. Only positive values indicate excess weight.</p>}
      faqs={[
        { q: "How fast should I lose weight?", a: "A safe rate is 0.5–1% of body weight per week, mostly by combining a modest calorie deficit with strength training." },
        { q: "Why 24.9 specifically?", a: "It's the top of the WHO 'normal' BMI range." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        {UnitInputs}
        <div className="grid gap-4 sm:grid-cols-2">{HeightInputs}{WeightInputs}</div>
      </div>
      {bmi > 0 && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <ResultCard label={toLose > 0 ? "Weight to lose" : "Great news"} value={toLose > 0 ? round(toLose, 1) : "0"} unit="kg" tone={toLose > 0 ? "warning" : "good"} testId="overweight-result">
            {toLose > 0 ? `to reach a BMI of 24.9` : `Already at/under BMI 24.9`}
          </ResultCard>
          <div className="rounded-2xl border border-border p-6">
            <div className="text-xs uppercase text-muted-foreground">Current BMI</div>
            <div className="mt-2 text-3xl font-bold">{round(bmi, 1)}</div>
            <div className="text-sm text-muted-foreground mt-1">Target: {round(target, 1)} kg</div>
          </div>
        </div>
      )}
    </CalculatorLayout>
  );
}
